import {
  CloseOutlined,
  EditOutlined,
  InfoCircleOutlined,
  SaveOutlined
} from "@ant-design/icons";
import { Button, Divider, Input, Popover, Select, Tag } from "antd";
import { message } from "utils/ciq-message";
import { useContext, useEffect, useMemo, useState } from "react";
import { updateOpcProjectConfig } from "services/integration-service";
import dayjs from "utils/dayjs-config";
import {
  isPermissionNotGrantted,
  ProjectContext,
  TProjectContext
} from "context/ProjectProvider";
import {
  ErrorMessages,
  IntegrationSystemNameMap,
  ProjectPermissionEnum
} from "../../../../constants";
import { ProjectIntegrationsTabSharedContext } from "../project-integrations-tab-context";

export default function ProjectOpcIntegrationDetails() {
  const {
    tokenRetrievalState,
    tokenContents,
    isProjectArchived
  }: TProjectContext = useContext(ProjectContext);

  const { sharedState, dispatch } = useContext(
    ProjectIntegrationsTabSharedContext
  );

  const canChangeIntegration = !isPermissionNotGrantted(
    ProjectPermissionEnum.changeProjectIntegration,
    tokenContents?.role ?? ""
  );

  const [isIntegrationNameInEditMode, setIsIntegrationNameInEditMode] =
    useState(false);
  const [isEmailInEditMode, setIsEmailInEditMode] = useState(false);
  const [alertEmails, setAlertEmails] = useState([]);

  const [saveIntgNameInProg, setSaveIntgNameInProg] = useState(false);
  const [savingEmailInProg, setSavingEmailInProg] = useState(false);
  const [integrationName, setIntegrationName] = useState("");

  const integrationDetails = useMemo(() => {
    return sharedState.projectIntegrationConfigs.find((config: any) => {
      return config.id === sharedState.additionalData.id;
    });
  }, [sharedState.additionalData.id, sharedState.projectIntegrationConfigs]);

  const integrationProperties = integrationDetails?.properties || {};
  const subscriptionIntegrationProperties =
    integrationDetails?.subscriptionProperties || {};

  const incomingAlertEmails = useMemo(() => {
    return (integrationProperties.PROJECT_ALERT_EMAIL || "").split(",");
  }, [integrationProperties.PROJECT_ALERT_EMAIL]);

  useEffect(() => {
    setAlertEmails(incomingAlertEmails.filter(Boolean));
  }, [incomingAlertEmails]);

  useEffect(() => {
    setIntegrationName(integrationProperties?.INTEGRATION_NAME);
  }, [integrationProperties?.INTEGRATION_NAME]);

  const saveField = async (
    payloadData: any,
    messages: { success: string; error: string }
  ) => {
    const payload: any = [
      {
        ...payloadData
      }
    ];

    const updateResponse = await updateOpcProjectConfig(
      integrationDetails.id,
      payload,
      tokenRetrievalState.token
    );

    if (updateResponse.success) {
      message.success(messages.success);
      setIsEmailInEditMode(false);
    } else {
      message.error(messages.error);
    }
    return updateResponse;
  };

  const resetEmailFieldState = () => {
    setIsEmailInEditMode(false);
    setAlertEmails(incomingAlertEmails.filter(Boolean));
  };

  const resetIntgNameFieldState = () => {
    setIsIntegrationNameInEditMode(false);
    setIntegrationName(integrationProperties.INTEGRATION_NAME);
  };

  const saveEmail = async () => {
    setSavingEmailInProg(true);
    await saveField(
      {
        properties: {
          project_alert_email: alertEmails.join(",")
        }
      },
      {
        success: "Notification recipients updated successfully.",
        error: "Failed to update notification recipients."
      }
    );
    setSavingEmailInProg(false);
  };

  const saveIntegName = async () => {
    setSaveIntgNameInProg(true);
    const updateResponse = await saveField(
      {
        properties: {
          integration_name: integrationName
        }
      },
      {
        success: "Project integration name updated successfully.",
        error: "Failed to update project integration name."
      }
    );
    setSaveIntgNameInProg(false);
    if (updateResponse?.success) {
      resetIntgNameFieldState();
    }
  };

  const updateInProgress = savingEmailInProg || saveIntgNameInProg;

  const lastAuthorized =
    integrationProperties.LAST_AUTHORIZED ||
    subscriptionIntegrationProperties.LAST_AUTHORIZED ||
    integrationProperties.LAST_UPDATED ||
    subscriptionIntegrationProperties.LAST_UPDATED;

  const opcAuthorizerName =
    integrationProperties.AUTHORIZER_OPC_NAME ||
    subscriptionIntegrationProperties.AUTHORIZER_OPC_NAME ||
    "";
  const opcAuthorizerEmail =
    integrationProperties.AUTHORIZER_OPC_EMAIL ||
    subscriptionIntegrationProperties.AUTHORIZER_OPC_EMAIL ||
    "";
  const ciqAuthorizerName =
    integrationProperties.AUTHORIZER_CIQ_NAME ||
    integrationProperties.SUBSCRIPTION_AUTHORIZER_CIQ_NAME ||
    subscriptionIntegrationProperties.AUTHORIZER_CIQ_NAME ||
    subscriptionIntegrationProperties.SUBSCRIPTION_AUTHORIZER_CIQ_NAME ||
    "";
  const ciqAuthorizerEmail =
    integrationProperties.AUTHORIZER_CIQ_EMAIL ||
    integrationProperties.SUBSCRIPTION_AUTHORIZER_CIQ_EMAIL ||
    subscriptionIntegrationProperties.AUTHORIZER_CIQ_EMAIL ||
    subscriptionIntegrationProperties.SUBSCRIPTION_AUTHORIZER_CIQ_EMAIL ||
    "";

  if (!integrationDetails) {
    return (
      <div className="h-full flex flex-col">
        <div className="grow w-full md:max-w-screen-sm mx-auto py-4">
          <div className="text-sm text-neutral-700">
            Integration details are unavailable.
          </div>
        </div>
        <Divider className="mx-0 pb-0 mb-0" />
        <footer className="p-6 flex justify-end gap-x-4">
          <Button
            size="middle"
            onClick={() => {
              dispatch({
                type: "SET_ACTIVE_LIST_VIEW"
              });
            }}
          >
            Back
          </Button>
        </footer>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="h-full relative">
        <div className="grow w-full md:max-w-screen-sm mx-auto py-4">
          <div className="mt-5 ">
            <div className="text-sm uppercase font-semibold text-neutral-700">
              You are authorized with{" "}
              {IntegrationSystemNameMap[sharedState.system]}
            </div>
            <div className="text-xs mt-1">
              <span className="text-neutral-600">Last authorized on: </span>
              <span>
                {lastAuthorized
                  ? dayjs(lastAuthorized).local().format("hh:mmA MM/DD/YYYY")
                  : null}
              </span>
            </div>
          </div>
          <div className="mt-8 w-[500px] space-y-5">
            <div>
              <div className="flex">
                <span className="form-field-heading-2">
                  Project Integration Name
                </span>
              </div>
              {isIntegrationNameInEditMode && (
                <div className="w-full flex items-start space-x-2">
                  <div className="w-full">
                    <Input
                      className="normal-case w-full"
                      disabled={saveIntgNameInProg}
                      value={integrationName}
                      onChange={(event: any) => {
                        setIntegrationName(event.target.value);
                      }}
                    />
                    {!integrationName.trim() && (
                      <div className="text-ciq-error-500">
                        Please enter the project integration name
                      </div>
                    )}
                  </div>
                  <Button
                    size="small"
                    className="bg-ciq-neutral-25 p-2 mt-1"
                    title="Save"
                    icon={<SaveOutlined />}
                    onClick={async () => {
                      saveIntegName();
                    }}
                    disabled={!integrationName.trim() || updateInProgress}
                    loading={saveIntgNameInProg}
                  />
                  <Button
                    size="small"
                    className="bg-ciq-neutral-25 mt-1"
                    title="Cancel"
                    onClick={resetIntgNameFieldState}
                    icon={<CloseOutlined />}
                    disabled={saveIntgNameInProg}
                  />
                </div>
              )}
              {!isIntegrationNameInEditMode && (
                <div className="mt-1 flex flex-wrap">
                  <div>
                    <Tag
                      key={integrationProperties.INTEGRATION_NAME}
                      className="tag-selected-item"
                    >
                      {integrationProperties.INTEGRATION_NAME}
                    </Tag>
                  </div>

                  <Button
                    size="small"
                    className="bg-ciq-neutral-25"
                    onClick={() => {
                      setIsIntegrationNameInEditMode(true);
                    }}
                    icon={<EditOutlined />}
                    disabled={!canChangeIntegration || updateInProgress}
                    title={
                      !canChangeIntegration
                        ? isProjectArchived
                          ? undefined
                          : ErrorMessages.PermissionNotGranted
                        : ""
                    }
                  />
                </div>
              )}
            </div>
            <div>
              <div className="flex">
                <span className="form-field-heading-2">Workspace Name</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-y-2">
                <Tag key={0} className="tag-selected-item">
                  {subscriptionIntegrationProperties?.OPC_WORKSPACE_NAME ||
                    integrationProperties.OPC_WORKSPACE_NAME}
                </Tag>
              </div>
            </div>
            <div>
              <div className="flex">
                <span className="form-field-heading-2">Project Name</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-y-2">
                <Tag key={0} className="tag-selected-item">
                  {integrationProperties.OPC_PROJECT_NAME}
                </Tag>
              </div>
            </div>
            <div>
              <div className="flex">
                <span className="form-field-heading-2">
                  Notification Recipients
                </span>
                <Popover
                  content={
                    <div className="text-sm font-medium">
                      Enter the email address of users who need to be notified
                      in case of failure in authorization.
                    </div>
                  }
                >
                  <InfoCircleOutlined className="pl-0.5 text-xs mt-0.5" />
                </Popover>
              </div>
              {isEmailInEditMode && (
                <div className="w-full flex items-start space-x-2">
                  <div className="w-full">
                    <Select
                      className="normal-case w-full"
                      mode="tags"
                      placeholder="Email IDs"
                      notFoundContent={null}
                      styles={{ popup: { root: { display: "none" } } }}
                      value={alertEmails}
                      onChange={(value: any) => {
                        setAlertEmails(value);
                      }}
                      disabled={savingEmailInProg || updateInProgress}
                    />
                    {alertEmails.length === 0 && (
                      <div className="text-ciq-error-500">
                        Please enter at least one email
                      </div>
                    )}
                  </div>
                  <Button
                    size="small"
                    className="bg-ciq-neutral-25 p-2 mt-1"
                    title="Save"
                    icon={<SaveOutlined />}
                    onClick={saveEmail}
                    disabled={
                      alertEmails.length === 0 ||
                      savingEmailInProg ||
                      updateInProgress
                    }
                    loading={savingEmailInProg}
                  />
                  <Button
                    size="small"
                    className="bg-ciq-neutral-25 mt-1"
                    title="Cancel"
                    onClick={resetEmailFieldState}
                    icon={<CloseOutlined />}
                    disabled={savingEmailInProg}
                  />
                </div>
              )}
              {!isEmailInEditMode && (
                <div className="mt-1 flex flex-wrap gap-y-2">
                  {alertEmails.map((email: string) => {
                    return (
                      <Tag key={email} className="tag-selected-item">
                        {email}
                      </Tag>
                    );
                  })}

                  <Button
                    size="small"
                    className="bg-ciq-neutral-25"
                    onClick={() => {
                      setIsEmailInEditMode(true);
                    }}
                    icon={<EditOutlined />}
                    disabled={!canChangeIntegration || updateInProgress}
                    title={
                      !canChangeIntegration
                        ? isProjectArchived
                          ? undefined
                          : ErrorMessages.PermissionNotGranted
                        : ""
                    }
                  />
                </div>
              )}
            </div>
          </div>
          <div className="mt-9 grid grid-cols-2 gap-x-4">
            <div className="space-y-1">
              <div className="info-label">
                AUTHORIZATION GRANTED BY (OPC USER):
              </div>
              <div className="info-value">
                {opcAuthorizerName || opcAuthorizerEmail || "—"}
                {opcAuthorizerName && opcAuthorizerEmail && (
                  <> ({opcAuthorizerEmail})</>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <div className="info-label">AUTHORIZATION INITIATED BY:</div>
              <div className="info-value">
                {ciqAuthorizerName
                  ? `${ciqAuthorizerName} (${ciqAuthorizerEmail})`
                  : "—"}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Divider className="mx-0 pb-0 mb-0" />
      <footer className="p-6 flex justify-end gap-x-4">
        <Button
          size="middle"
          onClick={() => {
            dispatch({
              type: "SET_ACTIVE_LIST_VIEW"
            });
          }}
        >
          Back
        </Button>
      </footer>
    </div>
  );
}
